import { XIcon } from '@phosphor-icons/react'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/business/market-shop-restaurant/button'
import { DeliveryAlert } from '@/components/business/market-shop-restaurant/delivery-alert'
import { NoteAlert } from '@/components/business/market-shop-restaurant/note-alert'
import { PaymentMethodAlert } from '@/components/business/market-shop-restaurant/payment-method-alert'
import { RelativeTime } from '@/components/business/market-shop-restaurant/relative-time'
import { Icon } from '@/components/ui/icon'
import { Overlay } from '@/components/ui/overlay'
import { Typography } from '@/components/ui/typography'
import {
  OrderDetailsModalContentProps,
  OrderDetailsModalProps,
} from '@/pages/market-shop-restaurant/order-details-modal/order-details-modal.types'
import { checkForScrollBar } from '@/utils'

import { OrderStatusControls } from '../orders-column/order-card/order-status-controls'
import * as S from './order-details-modal.styled'

export const OrderDetailsModal = ({ order, onClose }: OrderDetailsModalProps) => {
  return (
    <Overlay onClose={onClose}>
      {(overlayProps) => <OrderDetailsModalContent {...overlayProps} order={order} />}
    </Overlay>
  )
}

// NOTE: Separate `OrderDetailsModalContent` component is needed to get access to `OverlayBaseProps['children'] props
// as well as separate component lifecycle
const OrderDetailsModalContent = ({
  order,
  isInTransition,
  handleClose,
  getFloatingProps,
  setFloating,
}: OrderDetailsModalContentProps) => {
  const { t } = useTranslation(['market-shop-restaurant'])
  const { id, createdAt, items, orderNotes } = order

  const [hasVerticalScroll, setHasVerticalScroll] = useState(false)
  const ordersWrapperRef = useRef<HTMLUListElement>(null)

  useLayoutEffect(() => {
    const ordersWrapperElement = ordersWrapperRef.current
    if (!ordersWrapperElement || isInTransition) return

    const checkScrollable = () => {
      setHasVerticalScroll(checkForScrollBar(ordersWrapperElement, 'vertical'))
    }

    checkScrollable()

    const observer = new ResizeObserver(checkScrollable)
    observer.observe(ordersWrapperElement)
    return () => observer.disconnect()
  }, [isInTransition])

  return (
    <S.OrderDetailsModalContent ref={setFloating} {...getFloatingProps()}>
      <S.Header>
        <Typography
          as="h2"
          size="heading-huge"
          weight="semibold"
          color="black800"
          breakLines={true}
        >
          {t('market-shop-restaurant:orderDetails.title', { id })}
        </Typography>
        <RelativeTime createdAt={createdAt} />
        <Button
          onlyBox
          type="button"
          use="unset"
          aria-label={t('market-shop-restaurant:orderDetails.close')}
          disabled={isInTransition}
          onClick={handleClose}
        >
          <Icon icon={XIcon} size="2.4rem" />
        </Button>
      </S.Header>
      <S.Content>
        <S.OrderedItems ref={ordersWrapperRef} $hasVerticalScroll={hasVerticalScroll}>
          {items.map(({ id, productId, quantity, note }) => (
            <li key={id}>
              <S.OrderedItemRow>
                <Typography as="span" size="heading-medium" weight="medium" color="black800">
                  {t('market-shop-restaurant:orderItem.quantity', { quantity })}
                </Typography>
                <S.OrderedItemDetails>
                  <Typography
                    as="span"
                    size="heading-medium"
                    weight="medium"
                    color="black800"
                    breakLines={true}
                  >
                    {productId.summary}
                  </Typography>
                  {note && (
                    <S.OrderDescription
                      forwardedAs="span"
                      size="text-medium"
                      color="black500"
                      weight="medium"
                      wordBreak="break-word"
                    >
                      {note}
                    </S.OrderDescription>
                  )}
                </S.OrderedItemDetails>
              </S.OrderedItemRow>
            </li>
          ))}
        </S.OrderedItems>
        <S.Infos>
          {orderNotes && <NoteAlert text={orderNotes} size="heading-medium" />}
          <PaymentMethodAlert order={order} size="heading-medium" />
          <DeliveryAlert order={order} size="heading-medium" />
        </S.Infos>
        <OrderStatusControls order={order} size="large" disabled={isInTransition} />
      </S.Content>
    </S.OrderDetailsModalContent>
  )
}
